import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Chip,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import Circular from 'components/common/CustomCircular';
import { LanguageChip } from 'components/training';
import {
  LearnQuestionSummary,
  QuestionSourceType,
  listLearnAssignment,
  listLearnQuestions,
} from 'stores/TrainingQuestionStoreAdapter';
import { errorDefault } from 'utils/Toastify';

import LearnBreadcrumbs from './components/LearnBreadcrumbs';

/** 全設問（重複排除済み）の1件。設問詳細への遷移に使う assignmentId を保持する。 */
type MergedQuestion = LearnQuestionSummary & {
  /** この設問を含む課題のうち、遷移に使う assignmentId（最初に見つかった課題） */
  assignmentId: string;
};

type FilterKey = 'all' | 'cleared' | 'uncleared';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'uncleared', label: '未クリア' },
  { key: 'cleared', label: 'クリア済み' },
];

const normalizeFilter = (v: string | null): FilterKey =>
  v === 'cleared' || v === 'uncleared' ? v : 'all';

/**
 * 全設問一覧（受講）。
 * 受講者向けに「全設問を返すAPI」は無いため、割当課題一覧（API6351）＋各課題の設問一覧（API6352）を
 * フロントで合成し、questionId で重複排除して表示する（同じ設問が複数課題に含まれるため）。
 * クエリ ?filter=all|cleared|uncleared で絞り込み（ダッシュボードからのディープリンク対応）。
 */
const AllQuestions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = normalizeFilter(searchParams.get('filter'));

  const [questions, setQuestions] = React.useState<MergedQuestion[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const assignments = await listLearnAssignment();
        // 各課題の設問一覧を並行取得
        const perAssignment = await Promise.all(
          assignments.map((a) =>
            listLearnQuestions(a.assignmentId)
              .then((qs) => ({ assignmentId: a.assignmentId, questions: qs }))
              .catch(() => ({ assignmentId: a.assignmentId, questions: [] })),
          ),
        );
        // questionId で重複排除。cleared は「いずれかの課題でクリア」を採用し、
        // 言語別クリア状況も extension 単位で OR マージする。
        const map = new Map<string, MergedQuestion>();
        perAssignment.forEach(({ assignmentId, questions: qs }) => {
          qs.forEach((q) => {
            const existing = map.get(q.questionId);
            if (!existing) {
              map.set(q.questionId, { ...q, assignmentId });
              return;
            }
            // 言語別クリア状況をマージ（どちらかで cleared なら cleared）
            const langMap = new Map(existing.languages.map((l) => [l.extension, l.cleared]));
            q.languages.forEach((l) => {
              langMap.set(l.extension, (langMap.get(l.extension) ?? false) || l.cleared);
            });
            map.set(q.questionId, {
              ...existing,
              cleared: existing.cleared || q.cleared,
              languages: Array.from(langMap.entries()).map(([extension, cleared]) => ({
                extension,
                cleared,
              })),
            });
          });
        });
        setQuestions(Array.from(map.values()));
      } catch {
        errorDefault('設問一覧の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = React.useMemo(() => {
    const cleared = questions.filter((q) => q.cleared).length;
    return { all: questions.length, cleared, uncleared: questions.length - cleared };
  }, [questions]);

  const visible = React.useMemo(() => {
    if (filter === 'cleared') return questions.filter((q) => q.cleared);
    if (filter === 'uncleared') return questions.filter((q) => !q.cleared);
    return questions;
  }, [questions, filter]);

  const changeFilter = (next: FilterKey) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('filter');
        else p.set('filter', next);
        return p;
      },
      { replace: true },
    );
  };

  const emptyMessage =
    filter === 'cleared'
      ? 'クリア済みの設問はありません'
      : filter === 'uncleared'
        ? '未クリアの設問はありません'
        : '設問がありません';

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Circular open={loading} />
      <LearnBreadcrumbs items={[{ label: '研修', to: '/training-learn' }, { label: '全設問' }]} />
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>
        全設問
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        割り当てられた全課題の設問（重複を除く）
      </Typography>

      {/* 簡易フィルタ（すべて / 未クリア / クリア済み） */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filter}
        onChange={(_, next) => next && changeFilter(next as FilterKey)}
        sx={{
          mb: 2,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 1.75,
          },
        }}
      >
        {FILTERS.map((f) => (
          <ToggleButton key={f.key} value={f.key}>
            {f.label}
            <Box component="span" sx={{ ml: 0.75, color: 'text.secondary' }}>
              {counts[f.key]}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {!loading && visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
          {visible.map((q) => (
            <Paper
              key={q.questionId}
              elevation={0}
              onClick={() =>
                navigate(
                  `/training-learn/learn-assignment/${q.assignmentId}/question/${q.questionId}`,
                )
              }
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                {q.cleared && <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />}
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                  {q.title}
                </Typography>
                <Chip
                  size="small"
                  label={QuestionSourceType[q.sourceType]}
                  variant="outlined"
                  sx={{ borderRadius: 1, height: 20, fontSize: 11 }}
                />
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {q.languages.map((lang) => (
                  <LanguageChip
                    key={lang.extension}
                    extension={lang.extension}
                    cleared={lang.cleared}
                  />
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default AllQuestions;
